import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { db, auth, googleProvider } from './firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { 
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  updateProfile,
  getMultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  TotpMultiFactorGenerator,
  RecaptchaVerifier,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  sendPasswordResetEmail
} from "firebase/auth"; // For authentication
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, Link } from 'react-router-dom';
import Dashboard from './pages/Dashboard';
import AssessmentForm from './pages/AssessmentForm';
import Results from './pages/Results';
import { isFeatureEnabled, IS_DEMO } from './constants/featureFlags';
import { OnboardingProvider, useOnboarding, TOUR_TARGETS, SUPPORT_EMAIL, NAV_GROUP_LETTER_SOUND, NAV_GROUP_MANAGEMENT, NAV_GROUP_HELP } from './onboarding';
// Phonemic Awareness Diagnostic (PA-D, v3.72.0) — fully isolated module:
// index.js is its ONLY import surface, and routes/sidebar strings come from
// these constants so the /pa-diagnostic namespace lives inside the module.
import {
  PadAssessmentLazy,
  PadResultsLazy,
  PAD_ROUTES,
  PAD_SIDEBAR_GROUP,
  PAD_SIDEBAR_ICON,
  PAD_SIDEBAR_COLOR,
} from './pa-diagnostic';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import Groups from './pages/Groups';
import { isGroupVisible, FROZEN_HIDDEN } from './utils/sidebarVisibility';
import { getRosterLock, getDistrictSisName, rosterGuidanceText, clearRosterSourceCache } from './utils/rosterSource';
import OfflineBanner from './components/OfflineBanner';
import DemoBanner from './components/DemoBanner';
import DemoRoleSwitcher, { getDemoRole } from './components/DemoRoleSwitcher';
import ErrorBoundary from './components/ErrorBoundary';
import FeedbackModal from './components/FeedbackModal';
import FeedbackReplyNotice from './components/FeedbackReplyNotice';
import { useSessionFeedbackReplies } from './hooks/useFeedbackReplies';
import TrustedComputerNotice from './components/TrustedComputerNotice';
import {
  TRUST_DAYS, formatTrustDate, pickManualHint, signInWithTrustedComputer,
  tidyTrustedComputers, trustOfferEligible, trustThisComputer, trustedComputerSupported, usableHints,
} from './utils/trustedComputer';
import useTrustOfferFlag from './hooks/useTrustOfferFlag';
import useCloseMfaOnSignIn from './hooks/useCloseMfaOnSignIn';
import { lateAnswerDecision } from './utils/lateTrustedAnswer';
import ReleaseNotesModal from './components/ReleaseNotesModal';
import OnboardingModal from './components/OnboardingModal';
import { logSecurityEvent } from './services/Logger';
import { withTimeout, TimeoutError } from './utils/withTimeout';
import { loadProfileOnce } from './utils/profileLoad';
import { LOAD_TIMEOUT_MS } from './hooks/usePageLoad';
import { detachAllSnapshots } from './utils/snapshotRegistry';
import { clearSchoolViewCache } from './utils/schoolViewCache';
import { canSeeSchoolView } from './utils/schoolViewGate';
import { canSeeReports } from './utils/reportsGate';
import { canSeeGradeTeam, mayMountGradeTeamRoute } from './utils/gradeTeamGate';
import { getGradeTeamBuildings, clearGradeTeamSourceCache } from './utils/gradeTeamSource';
import { showsTeacherTabs } from './utils/teacherTabs';
import { whenWritesSettled } from './utils/pendingWrites';
import useIdleTimeout from './hooks/useIdleTimeout';
import IdleWarningModal from './components/IdleWarningModal';
import { resolveLandingRoute, resolveRoleLabel } from './utils/roleRouting';
// Licensing is enforced via Firestore in the onAuthStateChanged handler below.
import './App.css';

// Route-level code splitting (2026-07 perf audit, step 10). These routes are
// either admin/TLS-only, feature-flagged, placeholders, or public legal pages —
// a classroom teacher opens none of them in a normal session, yet every one of
// them shipped inside the single main bundle. The teacher hot path (dashboard,
// assessment, results, groups, HFW/CVC) stays eagerly imported on purpose: a
// Suspense fallback there would be a visible regression on a slow phone.
// Shown only while a split chunk is in flight. A chunk that fails to load
// throws, which the surrounding ErrorBoundary catches — so this is never a
// terminal state (2026-07 perf audit, step 10).
function RouteChunkFallback() {
  return <div className="loading-container" style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading…</div>;
}

const BenchmarkScreenerAssessment = lazy(() => import('./pages/BenchmarkScreenerAssessment'));
// Clever SSO (v3.40.0): pre-auth routes — a teacher only ever loads these
// chunks mid-login, so they stay out of the main bundle.
const CleverAuthStart = lazy(() => import('./pages/auth/CleverAuthStart'));
const CleverCallback = lazy(() => import('./pages/auth/CleverCallback'));
const BenchmarkScreenerResults = lazy(() => import('./pages/BenchmarkScreenerResults'));
// 4K PA Screener (v3.57.0) — flag-gated, demo-visible.
const PaScreener = lazy(() => import('./pages/pa/PaScreener'));
const PaScreenerResults = lazy(() => import('./pages/pa/PaScreenerResults'));
const PaGroups = lazy(() => import('./pages/pa/PaGroups'));
// Phonological Awareness (4K) — six standard-pattern assessments (v3.70.0),
// flag-gated, dev-only.
const Pa4kAssessment = lazy(() => import('./pages/pa4k/Pa4kAssessment'));
const Pa4kResults = lazy(() => import('./pages/pa4k/Pa4kResults'));
// Phonemic Awareness Diagnostic (PA-D) — flag-gated, dev-only; lazy
// factories exported by the module's own surface.
const PadAssessment = lazy(PadAssessmentLazy);
const PadResults = lazy(PadResultsLazy);
const BoyReadiness = lazy(() => import('./pages/boy/BoyReadiness'));
const ScreenSizeDebugger = lazy(() => import('./pages/ScreenSizeDebugger'));
const ResponsiveTestHarness = lazy(() => import('./pages/ResponsiveTestHarness'));
const AboutData = lazy(() => import('./pages/AboutData'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const TrustCenter = lazy(() => import('./pages/TrustCenter'));
const DataRequests = lazy(() => import('./pages/DataRequests'));
const Subprocessors = lazy(() => import('./pages/Subprocessors'));
const DolchAssessment = lazy(() => import('./pages/dolch/DolchAssessment'));
const DolchResults = lazy(() => import('./pages/dolch/DolchResults'));
const DolchPractice = lazy(() => import('./pages/dolch/DolchPractice'));
const NonsenseAssessment = lazy(() => import('./pages/nonsense-words/NonsenseAssessment'));
const NonsenseResults = lazy(() => import('./pages/nonsense-words/NonsenseResults'));
const NonsensePractice = lazy(() => import('./pages/nonsense-words/NonsensePractice'));
const SkillGapFinder = lazy(() => import('./pages/school/SkillGapFinder'));
const SchoolGroups = lazy(() => import('./pages/school/SchoolGroups'));
const SchoolView = lazy(() => import('./pages/school/SchoolView'));
const GradeReport = lazy(() => import('./pages/school/GradeReport'));
const ClassReport = lazy(() => import('./pages/reports/ClassReport'));
const StudentAssessmentReport = lazy(() => import('./pages/reports/StudentAssessmentReport'));
const GradeTeam = lazy(() => import('./pages/reports/GradeTeam'));
const RecommendedGroups = lazy(() => import('./pages/school/RecommendedGroups'));
// v3.37.2 bundle-reduction: non-hot-path teacher routes moved off the eager
// bundle (Dashboard, AssessmentForm, Results, Groups stay eager).
const StudentUpload = lazy(() => import('./pages/StudentUpload'));
const DisplaySettings = lazy(() => import('./pages/DisplaySettings'));
const PracticeForm = lazy(() => import('./pages/PracticeForm'));
const UserGuide = lazy(() => import('./pages/UserGuide'));
const StudentReport = lazy(() => import('./pages/StudentReport'));
const AssessmentManagement = lazy(() => import('./pages/AssessmentManagement'));
const HFWAssessment = lazy(() => import('./pages/hfw/HFWAssessment'));
const HFWResults = lazy(() => import('./pages/hfw/HFWResults'));
const HFWPractice = lazy(() => import('./pages/hfw/HFWPractice'));
const HFWGroups = lazy(() => import('./pages/hfw/HFWGroups'));
const CvcAssessment = lazy(() => import('./pages/cvc/CvcAssessment'));
const CvcResults = lazy(() => import('./pages/cvc/CvcResults'));
const CvcPractice = lazy(() => import('./pages/cvc/CvcPractice'));
const CvcGroups = lazy(() => import('./pages/cvc/CvcGroups'));

function App() {
  const [user, setUser] = useState(null);
  // perf-lint-allow: auth-boot shell — driven by onAuthStateChanged (not a promise, so usePageLoad does not apply) and already bounded by the 20s global timeout below plus the 12s impersonation one (docs/perf/PERFORMANCE.md)
  const [loading, setLoading] = useState(true);
  const [mfaResolver, setMfaResolver] = useState(null);
  // Boot-stall recovery (v3.69.2, the idle+MFA re-login wedge): set when the
  // auth boot pipeline fails or times out while Firebase IS authenticated.
  // Rendered as a visible retryable state — never the Sign In card, which
  // used to sit there silently over a live session.
  const [bootStall, setBootStall] = useState(null);
  const [activeMfaFactor, setActiveMfaFactor] = useState(null);
  // v3.91.0 — "Don't ask for a code on this computer for 30 days" (utils/trustedComputer.js).
  const [trustComputer, setTrustComputer] = useState(false);
  const [trustNotice, setTrustNotice] = useState(null);
  // v3.91.0: one trusted-computer answer at a time, and which sign-in attempt
  // is current — a late answer for an attempt that was cancelled (or
  // superseded) must not leave anyone signed in.
  const trustedAnswerInFlight = useRef(false);
  const mfaAttempt = useRef(0);
  const signedInAttempt = useRef(0); // the latest attempt that completed a sign-in in this tab
  const signedInUid = useRef(null);   // …and its account (both reset by Logout)
  const appUid = useRef(null);        // the account the app is showing right now
  const [mfaCode, setMfaCode] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(sessionStorage.getItem('isImpersonating') === 'true');
  const [extraMetadata, setExtraMetadata] = useState({ profile: null, role: null }); // Store demographics and claims separately to keep Firebase User instance stable
  // Demo-only: which role the demo account is currently "viewing as". Kept in
  // state so the role switcher updates the view instantly (no page reload).
  const [demoRole, setDemoRole] = useState(IS_DEMO ? getDemoRole() : null);

  // adminRestoreToken is kept in memory only (never sessionStorage) to reduce XSS exposure.
  // It is passed via the redirect URL and captured here immediately, then the URL is cleaned.
  const adminRestoreTokenRef = useRef(null);
  
  // Tracks whether onAuthStateChanged has delivered a definitive auth state.
  // Prevents the brief 'null gap' flash to the landing page during page reloads
  // where Firebase hasn't yet restored the persisted session.
  const authResolvedRef = useRef(false);
  const recaptchaRef = useRef(null);
  // Idle-session plumbing (v3.56.0). Declared with the other refs so
  // handleLogout can reach the tracker without a forward reference.
  const idleRef = useRef(null);
  const teardownRef = useRef(false);

  // The navigation panel is visible by default on larger screens.
  const [isNavOpen, setIsNavOpen] = useState(window.innerWidth > 768);

  // Function to toggle the navigation panel
  const toggleNav = () => {
    setIsNavOpen(!isNavOpen);
  };

  useEffect(() => {
    document.title = 'Early Literacy Assessment';
    
    // Safety Timeout: Force loading screen to clear after 20 seconds if it hangs
    const globalLoadingTimeout = setTimeout(() => {
      setLoading(false);
      console.warn("LITERACY-APP-WARN: Global loading timeout reached (20s). Forcing UI to unlock.");
    }, 20000);

    // Hidden Admin Impersonation Capability
    const urlParams = new URLSearchParams(window.location.search);
    const impersonateToken = urlParams.get('impersonateToken');
    // Capture the admin restore token from URL into memory (never stored in sessionStorage)
    const adminTokenFromUrl = urlParams.get('adminToken');
    if (adminTokenFromUrl) {
      adminRestoreTokenRef.current = adminTokenFromUrl;
    }

    if (impersonateToken) {
      if (process.env.NODE_ENV === 'development') {
        console.log("LITERACY-APP-INFO: Impersonation token detected. Handshaking...");
      }
      setLoading(true);
      // Ensure the pending flag is set if it wasn't already (e.g. direct link)
      sessionStorage.setItem('impersonation_pending', 'true');

      // Add a safety timeout to prevent infinite loading if the network hangs
      const safetyTimeout = setTimeout(() => {
        if (process.env.NODE_ENV === 'development') {
          console.warn("LITERACY-APP-WARN: Impersonation transition timed out.");
        }
        sessionStorage.removeItem('impersonation_pending');
        setLoading(false);
      }, 12000);

      signInWithCustomToken(auth, impersonateToken)
        .then(() => {
          clearTimeout(safetyTimeout);
          sessionStorage.setItem('isImpersonating', 'true');
          setIsImpersonating(true);
          
          // CRITICAL: We don't call setLoading(false) here yet. 
          // We let the onAuthStateChanged listener handle the metadata fetch for the NEW user
          // and it will call setLoading(false) once the profile is ready.
          
          // Remove both tokens from URL immediately
          window.history.replaceState({}, document.title, window.location.pathname);
          if (process.env.NODE_ENV === 'development') {
            console.log("LITERACY-APP-SUCCESS: Impersonation token consumed. Waiting for metadata sync.");
          }
        })
        .catch(err => {
          clearTimeout(safetyTimeout);
          sessionStorage.removeItem('impersonation_pending');
          console.error("LITERACY-APP-ERROR: Impersonation failed.");
          alert("Impersonation failed: " + err.message);
          setLoading(false);
        });
    }

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      try {
        if (currentUser) {
          setBootStall(null); // fresh pipeline run — clear any prior stall
          // 1. License Check
          try {
            const email = currentUser.email || '';
            
            // Bypass license check for impersonated local tokens that don't pass an email claim
            // BYPASS LICENSE CHECK FOR IMPERSONATION
            // If an administrator is impersonating a user, we bypass the standard license check.
            const isImpersonatingSession = sessionStorage.getItem('isImpersonating') === 'true' || 
                                          sessionStorage.getItem('impersonation_pending') === 'true';
            
            if (isImpersonatingSession) {
              if (process.env.NODE_ENV === 'development') {
                 console.log("LITERACY-APP-INFO: Bypassing license check for impersonated session.");
              }
            } else if (!email) {
               console.warn("LITERACY-APP-AUTH: Logging in anonymous user without license (Standard User).");
            } else {
              const emailParts = email.split('@');
              const domain = emailParts.length === 2 ? emailParts[1] : '';

              let isLicensed = false;
              let isExpired = false;

              const validateLicense = (snap) => {
                if (!snap.exists() || !snap.data().active) return null;
                const data = snap.data();
                if (data.expirationDate) {
                  const exp = data.expirationDate.toDate();
                  if (exp < new Date()) return 'expired';
                }
                return 'active';
              };

              // Check exact email match. Bounded (v3.69.2): a stalled channel
              // here used to hang the whole boot with the Sign In card up.
              const emailLicenseSnap = await withTimeout(
                getDoc(doc(db, 'licenses', email)), LOAD_TIMEOUT_MS, 'license check (email)');
              const emailStatus = validateLicense(emailLicenseSnap);

              if (emailStatus === 'active') {
                isLicensed = true;
              } else if (emailStatus === 'expired') {
                isExpired = true;
              }

              // Check domain match
              if (!isLicensed && !isExpired && domain) {
                const domainLicenseSnap = await withTimeout(
                  getDoc(doc(db, 'licenses', domain)), LOAD_TIMEOUT_MS, 'license check (domain)');
                const domainStatus = validateLicense(domainLicenseSnap);
                if (domainStatus === 'active') {
                  isLicensed = true;
                } else if (domainStatus === 'expired') {
                  isExpired = true;
                }
              }

              if (isExpired) {
                console.warn(`LITERACY-APP-AUTH: License expired for: ${email}`);
                await logSecurityEvent(currentUser, 'EXPIRED_LICENSE_LOGIN_ATTEMPT', { email });
                await signOut(auth);
                setUser(null);
                alert(`Access Denied: The license for ${email} has expired. Please contact the administrator.`);
                return;
              }

              if (!isLicensed) {
                console.warn(`LITERACY-APP-AUTH: Unauthorized access for: ${email}`);
                await logSecurityEvent(currentUser, 'UNAUTHORIZED_LOGIN', { email });
                await signOut(auth);
                setUser(null);
                alert(`Access Denied: The account (${email}) does not have an active license.`);
                return;
              }
            }
          } catch (licenseErr) {
            console.error("LITERACY-APP-ERROR: License verification failed:", licenseErr);
            // During impersonation, license permission errors are non-fatal
            // (e.g., EA tokens can't read the licenses collection under old rules).
            const isImpersonatingFallback = sessionStorage.getItem('isImpersonating') === 'true' ||
                                           sessionStorage.getItem('impersonation_pending') === 'true';
            if (isImpersonatingFallback) {
              console.warn("LITERACY-APP-WARN: Ignoring license error during impersonated session.");
            } else if (licenseErr?.name === 'TimeoutError') {
              // A hang is "state unknown", not "no license" — never a silent
              // sign-out. Propagate to the boot-stall handler (v3.69.2).
              throw licenseErr;
            } else if (licenseErr.code !== 'unavailable') {
               await signOut(auth);
               setUser(null);
               return;
            }
          }

          if (!currentUser.displayName && currentUser.providerData[0]?.displayName) {
            updateProfile(currentUser, { displayName: currentUser.providerData[0].displayName }).catch(console.error);
          }

          // 2. Fetch Custom Claims. All data-page queries wait on this step
          // (setUser fires only after it), so no query can run before role
          // claims are resolved.
          const applyClaims = (claims) => {
            currentUser.role = {
              admin: !!claims.admin,
              districtAdmin: claims.districtAdmin || null,
              districtAdminId: claims.districtAdminId || null,
              teacher: !!claims.teacher,
              ea: !!claims.ea,
              tls: !!claims.tls
            };
          };
          const hasRoleClaim = (claims) =>
            !!(claims.admin || claims.teacher || claims.ea || claims.tls || claims.districtAdmin || claims.districtAdminId);
          try {
            let idTokenResult = await withTimeout(
              currentUser.getIdTokenResult(), LOAD_TIMEOUT_MS, 'claims (cached token)'); // cached token first — cheap
            if (!hasRoleClaim(idTokenResult.claims)) {
              // The cached token may predate an admin's role grant (claims-lag,
              // 2026-07 audit step 5): force ONE refresh before concluding the
              // user is role-less. One-shot only — an unconditional force
              // refresh here previously caused infinite rendering loops.
              idTokenResult = await withTimeout(
                currentUser.getIdTokenResult(true), LOAD_TIMEOUT_MS, 'claims (forced refresh)');
            }
            applyClaims(idTokenResult.claims);
            if (process.env.NODE_ENV === 'development') {
            console.log("LITERACY-APP-INFO: Resolved Claims:", currentUser.role);
          }
          } catch (error) {
            // Don't proceed silently role-less on a transient failure — one
            // forced retry; if that also fails, pages show their access-error
            // state instead of firing rules-denied queries forever.
            console.warn("LITERACY-APP-AUTH: Claims fetch failed — retrying with a forced refresh", error);
            try {
              applyClaims((await withTimeout(
                currentUser.getIdTokenResult(true), LOAD_TIMEOUT_MS, 'claims (retry refresh)')).claims);
            } catch (retryError) {
              console.error("LITERACY-APP-AUTH: Claims unavailable after forced refresh.", retryError);
            }
          }

          // 3. Fetch Profile Data to enforce Onboarding.
          // v3.84.8: "doc does not exist" (→ profile null → onboarding) is
          // distinguished from "read failed" — loadProfileOnce retries a
          // failed read once after a short backoff and then THROWS, so the
          // global catch below shows BootStallNotice (Try again + the
          // signed-in email) instead of the OnboardingModal. A timed-out read
          // still throws at once (v3.69.2). A failed read is never "no profile".
          const { profile } = await loadProfileOnce(() => withTimeout(
            getDoc(doc(db, 'users', currentUser.uid)), LOAD_TIMEOUT_MS, 'profile load'));
          currentUser.profile = profile; // object, or null = confirmed missing
          if (profile) {
            // Inactivity tracking check: update lastActiveAt at most once per 24h.
            // Skipped on demo: the write lockdown denies users-doc writes, so this
            // fired a guaranteed permission-denied on every login — pure console
            // noise, and the trigger that stalled WebKit's Firestore channel
            // (see src/firebase.js isAppleWebKit note, 2026-07-26).
            const lastActive = profile.lastActiveAt?.toMillis() || 0;
            const now = Date.now();
            if (!IS_DEMO && now - lastActive > 24 * 60 * 60 * 1000) {
               updateDoc(doc(db, 'users', currentUser.uid), { lastActiveAt: serverTimestamp() }).catch(console.error);
               // Keep local profile up to date for this session
               profile.lastActiveAt = { toMillis: () => now };
            }
          }

          // Fire-and-forget (v3.69.2): the security log must never gate login
          // — its addDoc used to be awaited and could stall the whole boot.
          logSecurityEvent(currentUser, 'SESSION_RESTORED').catch(() => {});
          authResolvedRef.current = true;
          setBootStall(null);
          setExtraMetadata({ profile: currentUser.profile, role: currentUser.role });
          setUser(currentUser);
        } else {
          // NULL USER PATH
          // v3.91.0: whatever ended the session (Logout and the idle sign-out
          // clear these themselves; a sign-out Firebase starts does not), the
          // per-session caches never outlive it.
          clearSchoolViewCache();
          clearRosterSourceCache();
          clearGradeTeamSourceCache();
          // On the very first auth check after a page load, Firebase may briefly fire
          // a null event before restoring the persisted session. If auth hasn't resolved
          // yet, wait a short grace period before committing to the logged-out state.
          // This prevents the flash to the landing page during impersonation transitions.
          if (!authResolvedRef.current) {
            if (process.env.NODE_ENV === 'development') {
              console.log("LITERACY-APP-INFO: Waiting for auth to settle before showing logged-out state.");
            }
            // Give Firebase SDK up to 1.5s to restore the persisted session.
            // If a real user event arrives in that window, this timeout is harmless
            // because setUser will just overwrite the null.
            setTimeout(() => {
              if (!authResolvedRef.current) {
                authResolvedRef.current = true;
                setExtraMetadata({ profile: null, role: null });
                setUser(null);
                setLoading(false);
              }
            }, 1500);
            return; // Don't clear loading yet
          }
          setBootStall(null); // definitively signed out — the stall notice would lie
          setExtraMetadata({ profile: null, role: null });
          setUser(null);
        }
      } catch (globalErr) {
        console.error("LITERACY-APP-ERROR: Global auth flow error:", globalErr);
        // v3.69.2: a mid-boot failure with a LIVE Firebase session used to
        // strand the Sign In card over an authenticated user (the idle+MFA
        // re-login wedge). Surface a visible, retryable state instead.
        if (auth.currentUser) {
          authResolvedRef.current = true;
          setBootStall({
            email: auth.currentUser.email || '',
            detail: String(globalErr?.message || globalErr),
          });
        }
      } finally {
        if (authResolvedRef.current || (auth.currentUser != null)) {
          authResolvedRef.current = true;

          // Cleanup pending impersonation flag if we reached a non-null user 
          // (which would be the target in a handshake, or a normal session restore)
          const isPending = sessionStorage.getItem('impersonation_pending') === 'true';
          if (isPending && auth.currentUser) {
            if (process.env.NODE_ENV === 'development') {
              console.log("LITERACY-APP-SUCCESS: Handshake complete. UI ready.");
            }
            sessionStorage.removeItem('impersonation_pending');
          }
          
          setLoading(false);
        }
      }
    });
    return () => {
      unsubscribe();
      clearTimeout(globalLoadingTimeout);
    };
  }, []);

  const handleGoogleLogin = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const success = await processLoginSuccess(result.user);
      if (!success) {
        // License check already showed an alert and signed out
        return;
      }
    } catch (err) {
      if (err.code === 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(auth, err);

        // v3.91.0: a computer the global administrator trusted answers the
        // code itself (utils/trustedComputer.js). Nothing to answer, out of
        // date, or refused → null, and the code screen below takes over with
        // the same resolver.
        // One answer at a time: a second click meanwhile opens a popup, but
        // its sign-in stops here.
        if (trustedAnswerInFlight.current) return;
        const attempt = ++mfaAttempt.current;
        trustedAnswerInFlight.current = true;
        let trusted = null;
        try {
          const answering = signInWithTrustedComputer(resolver);
          trusted = await withTimeout(answering, LOAD_TIMEOUT_MS, 'trusted computer').catch(() => {
            // Too slow: the code screen takes over. If the answer still lands,
            // the session is signed in — kept (the code screen closes when the
            // app has a user) unless that sign-in was cancelled meanwhile.
            answering.then((late) => {
              if (!late) return;
              const lateUser = late.credential.user;
              const details = { factorName: late.factor.displayName || '', late: true };
              const decision = lateAnswerDecision({
                attempt,
                currentAttempt: mfaAttempt.current,
                signedInAttempt: signedInAttempt.current,
                signedInUid: signedInUid.current,
                lateUid: lateUser.uid,
                appUid: appUid.current,
              });
              if (decision === 'keep') {
                // utils/lateTrustedAnswer.js: its code screen is still open (it
                // closes now that the app has a user), or a newer sign-in of
                // the same account already owns the session.
                if (signedInAttempt.current < attempt) { signedInAttempt.current = attempt; signedInUid.current = lateUser.uid; }
                logSecurityEvent(lateUser, 'MFA_TRUSTED_COMPUTER_USED', details).catch(() => {});
                return;
              }
              // Undo it. The log write gets at most 3 s — the sign-out must not
              // wait on it — and the sign-out only goes ahead if the late
              // sign-in is still the session (a newer one may have replaced it).
              const logged = logSecurityEvent(lateUser, 'MFA_TRUSTED_COMPUTER_USED', { ...details, cancelled: true }).catch(() => {});
              Promise.race([logged, new Promise((resolve) => setTimeout(resolve, 3000))])
                .finally(() => {
                  if (auth.currentUser !== lateUser) return;
                  // Logout's listener detach and cache clears (not its other-tab
                  // broadcast, idle-state or impersonation clean-up): nothing
                  // cached for this session may carry over to the next one.
                  detachAllSnapshots();
                  clearSchoolViewCache();
                  clearRosterSourceCache();
                  clearGradeTeamSourceCache();
                  signOut(auth).catch(() => {});
                });
            });
            return null;
          });
        } finally {
          trustedAnswerInFlight.current = false;
        }
        if (trusted) {
          signedInAttempt.current = attempt;
          signedInUid.current = trusted.credential.user.uid;
          console.log('LITERACY-APP-INFO: MFA answered by a trusted computer.');
          // Recorded now: an unlicensed session is signed out below, and a
          // stalled license check must not lose the record.
          logSecurityEvent(trusted.credential.user, 'MFA_TRUSTED_COMPUTER_USED', { factorName: trusted.factor.displayName || '' }).catch(() => {});
          try {
            await withTimeout(processLoginSuccess(trusted.credential.user), LOAD_TIMEOUT_MS, 'post-MFA license check');
          } catch (postErr) {
            console.warn('LITERACY-APP-WARN: post-MFA license check failed or stalled — deferring to the boot pipeline.', postErr);
          }
          return;
        }

        setTrustComputer(false);
        setMfaResolver(resolver);
        
        // Start MFA challenge
        try {
          // Prioritize TOTP if available — an authenticator app, never a
          // trusted computer (no one can type a trusted computer's code).
          const selectedHint = pickManualHint(resolver.hints) || resolver.hints[0];
          
          console.log("LITERACY-APP-INFO: MFA Selected Hint:", selectedHint.factorId, "UID:", selectedHint.uid);
          
          if (selectedHint.factorId === 'phone') {
            if (recaptchaRef.current) {
                try { recaptchaRef.current.clear(); } catch(e) {}
                recaptchaRef.current = null;
            }
            recaptchaRef.current = new RecaptchaVerifier(auth, 'mfa-recaptcha-container', {
              size: 'invisible'
            });
            
            const phoneInfoOptions = {
              multiFactorHint: selectedHint,
              session: resolver.session
            };
            const phoneAuthProvider = new PhoneAuthProvider(auth);
            const vId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaRef.current);
            setActiveMfaFactor({
              type: 'phone',
              vId: vId,
              hint: selectedHint
            });
          } else {
            setActiveMfaFactor({
              type: 'totp',
              vId: 'totp',
              hint: selectedHint
            });
          }
        } catch (mfaErr) {
          console.error("MFA Start error:", mfaErr);
          if (mfaErr.message.includes('reCAPTCHA client element has been removed') || mfaErr.code === 'auth/argument-error') {
              window.location.reload();
          } else {
              alert("Failed to start MFA challenge: " + mfaErr.message);
          }
        }
      } else if (err.code !== 'permission-denied' && !err.message.includes('No active license found')) {
        console.error('Google login error:', err);
        alert('Google login failed: ' + err.message);
      }
    }
  };

  const handleEmailLogin = async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const success = await processLoginSuccess(result.user);
      if (!success) {
        return;
      }
    } catch (err) {
      console.error('Email login error:', err);
      // More user-friendly error message
      alert('Login failed: Invalid email or password.');
    }
  };

  const handleForgotPassword = async (email) => {
    if (!email) return;
    try {
      await sendPasswordResetEmail(auth, email);
      alert(`Password reset email sent to ${email}. Check your inbox (and spam folder).`);
    } catch (err) {
      // Don't reveal whether the email exists — give a neutral message
      alert('If that email has an account, a reset link has been sent.');
    }
  };

  const handleMfaSwitch = async () => {
    if (!mfaResolver || !activeMfaFactor) return;
    
    // Toggle between hints
    const currentFactorId = activeMfaFactor.hint.factorId;
    const otherHint = usableHints(mfaResolver.hints).find(h => h.factorId !== currentFactorId);
    
    if (otherHint) {
      console.log("LITERACY-APP-INFO: Switching MFA to:", otherHint.factorId, "UID:", otherHint.uid);
      setMfaCode('');
      
      if (otherHint.factorId === 'phone') {
        const verifier = new RecaptchaVerifier(auth, 'mfa-recaptcha-container', {
          size: 'invisible'
        });
        const phoneInfoOptions = {
          multiFactorHint: otherHint,
          session: mfaResolver.session
        };
        const vId = await new PhoneAuthProvider(auth).verifyPhoneNumber(phoneInfoOptions, verifier);
        setActiveMfaFactor({
          type: 'phone',
          vId: vId,
          hint: otherHint
        });
      } else {
        setActiveMfaFactor({
          type: 'totp',
          vId: 'totp',
          hint: otherHint
        });
      }
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    if (!mfaResolver || !activeMfaFactor) {
      console.error("LITERACY-APP-ERROR: MFA Submit called without resolver or factor.");
      return;
    }

    setMfaLoading(true);
    console.log(`LITERACY-APP-INFO: Submitting MFA for ${activeMfaFactor.type}. Code prefix: ${mfaCode.substring(0, 2)}...`);

    try {
      let multiFactorAssertion;
      if (activeMfaFactor.type === 'totp') {
        multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
          activeMfaFactor.hint.uid,
          mfaCode
        );
      } else {
        const cred = PhoneAuthProvider.credential(activeMfaFactor.vId, mfaCode);
        multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
      }
      
      const result = await mfaResolver.resolveSignIn(multiFactorAssertion);
      signedInAttempt.current = mfaAttempt.current;
      signedInUid.current = result.user.uid;
      console.log("LITERACY-APP-SUCCESS: MFA Resolved successfully.");
      // v3.69.2: keep the modal (its "Verifying..." state) up until the
      // post-login license check settles — closing it first left a bare
      // Sign In card during a stalled check (the idle+MFA re-login wedge).
      // Bounded, and its failure must never reach the invalid-code alert
      // below: the code was correct, resolveSignIn already succeeded — the
      // boot pipeline / stall notice owns recovery from here.
      let licensed = false;
      let licenseError = null;
      try {
        licensed = await withTimeout(processLoginSuccess(result.user), LOAD_TIMEOUT_MS, 'post-MFA license check');
      } catch (postErr) {
        licenseError = postErr;
        console.warn('LITERACY-APP-WARN: post-MFA license check failed or stalled — deferring to the boot pipeline.', postErr);
      }
      const wantsTrust = trustComputer;
      if (wantsTrust && licenseError) {
        setTrustNotice({
          tone: 'warn',
          text: licenseError instanceof TimeoutError
            ? "This computer was not trusted: the sign-in took too long to finish. Tick the box again at your next sign-in."
            : "This computer was not trusted: the sign-in did not finish normally. Tick the box again at your next sign-in.",
        });
      }
      setMfaResolver(null);
      setActiveMfaFactor(null);
      setMfaCode('');
      setTrustComputer(false);
      // v3.91.0: after a sign-in WITH a code — remove trusted computers past
      // their 30 days, then trust this one if the box was ticked. In the
      // background: it never holds up or undoes the sign-in.
      // Only while this sign-in is still the session: the boot pipeline's
      // own license check (which also checks the expiry date) may have
      // signed it out meanwhile. trustThisComputer re-checks before enrolling.
      if (licensed && auth.currentUser?.uid === result.user.uid) {
        const signedIn = result.user;
        (async () => {
          // Trust first, tidy after: a renewal must add the new trusted
          // computer before an old one goes (removing the account's newest
          // factor signs the session out — utils/trustedComputer.js).
          let trustFailed = false;
          if (wantsTrust) {
            try {
              const { until } = await trustThisComputer(signedIn);
              setTrustNotice({ tone: 'ok', text: `This computer is trusted until ${formatTrustDate(until)}. You won't be asked for a code here until then.` });
            } catch (trustErr) {
              trustFailed = true;
              setTrustNotice({ tone: 'error', text: trustErr.message });
            }
          }
          const { waiting } = await tidyTrustedComputers(signedIn);
          if (waiting.length > 0 && !trustFailed) {
            setTrustNotice({
              tone: 'warn',
              text: `An expired trusted computer is still on your account (${waiting.join('; ')}). Remove it in Admin → Security Settings — Firebase may sign you out once when you do.`,
            });
          }
        })();
      }
    } catch (err) {
      console.error("LITERACY-APP-ERROR: MFA Failed:", err);
      alert("Invalid code or MFA failed: " + err.message);
    }
    setMfaLoading(false);
  };

  const processLoginSuccess = async (authUser) => {
    if (!authUser) return false;
      // Enforce Dynamic Licensing on new login
      const emailParts = authUser.email.split('@');
      const domain = emailParts.length === 2 ? emailParts[1] : '';
      let isLicensed = false;

      // 1. Check exact email match
      const emailLicenseRef = doc(db, 'licenses', authUser.email);
      const emailLicenseSnap = await getDoc(emailLicenseRef);
      if (emailLicenseSnap.exists() && emailLicenseSnap.data().active) {
        isLicensed = true;
      }

      // 2. Check domain match (if email didn't match)
      if (!isLicensed && domain) {
        const domainLicenseRef = doc(db, 'licenses', domain);
        const domainLicenseSnap = await getDoc(domainLicenseRef);
        if (domainLicenseSnap.exists() && domainLicenseSnap.data().active) {
          isLicensed = true;
        }
      }

      if (!isLicensed) {
        console.warn('LITERACY-APP-TEST: License resolution failed for:', authUser.email, 'Domain:', domain);
        await logSecurityEvent(authUser, 'UNAUTHORIZED_LOGIN_ATTEMPT', { reason: 'No active license found', email: authUser.email, domain });
        await signOut(auth);
        alert(`Access Denied: The account (${authUser.email}) does not have an active license. Please contact the administrator.`);
        return false;
      }

      console.log('LITERACY-APP-TEST: License successfully resolved for:', authUser.email, 'Domain:', domain);
      await logSecurityEvent(authUser, 'LOGIN_SUCCESS', { provider: 'google.com', email: authUser.email, domain });

      if (authUser && !authUser.displayName && authUser.providerData[0]?.displayName) {
        await updateProfile(authUser, { displayName: authUser.providerData[0].displayName });
      }
      return true;
  };

  const handleLogout = async () => {
    setTrustNotice(null); // never leave a trusted-computer notice for the next person
    // A trusted-computer answer still on its way must not sign anyone back in.
    mfaAttempt.current += 1;
    signedInAttempt.current = 0;
    signedInUid.current = null;
    try {
      if (user) {
        await logSecurityEvent(user, 'LOGOUT_SUCCESS', { wasImpersonating: isImpersonating });
      }
      // An explicit logout in THIS tab must end the session in every tab
      // (shared-device requirement, same channel the idle timeout uses).
      idleRef.current?.broadcastSignOut('logout');
      idleRef.current?.clearIdleState();
      // Detach every live Firestore listener BEFORE invalidating the token —
      // listeners that outlive signOut are re-evaluated unauthenticated and
      // each logs a server-side permission-denied (2026-07 audit, step 3c#2).
      detachAllSnapshots();
      clearSchoolViewCache(); // School View's session cache dies with the token (v3.78.0)
      // v3.80.0: the roster-lock + Grade Team session probes die with it too — on a
      // shared device the next teacher must never see the previous answer.
      clearRosterSourceCache();
      clearGradeTeamSourceCache();
      sessionStorage.removeItem('isImpersonating');
      // Also clear the handshake flag — otherwise an interrupted impersonation
      // (e.g. exit with a lost in-memory restore token, which falls back to
      // handleLogout) leaves 'impersonation_pending' set, and ProtectedLayout
      // would render a permanent "Loading data..." for the now-logged-out user.
      sessionStorage.removeItem('impersonation_pending');
      adminRestoreTokenRef.current = null;
      setIsImpersonating(false);
      await signOut(auth);
      // Removed alert for a smoother user experience
    } catch (err) {
      console.error('Logout error:', err);
      alert('Logout failed. Please try again.');
    }
  };

  /**
   * Idle-session teardown (v3.56.0). Runs on expiry in THIS tab and when a
   * sibling tab broadcasts a timeout/logout.
   *
   * Order matters:
   *  1. Wait for any in-flight assessment write to settle — signing out
   *     mid-submission invalidates the token underneath the write and the
   *     teacher loses the scores they just collected. Bounded (10s) so a
   *     wedged write cannot strand a session signed-in on a shared device.
   *  2. Detach listeners BEFORE signOut (the existing 2026-07 audit rule).
   *  3. signOut, clear session-scoped storage, drop the shared timestamp.
   *  4. Hard-navigate to /login?reason=timeout. A full navigation (rather
   *     than a client-side route change) is deliberate: it guarantees every
   *     roster/class/assessment object still held in React state or a
   *     closure is discarded with the JS heap.
   */
  const handleIdleExpiry = async (reason = 'timeout') => {
    if (teardownRef.current) return; // one teardown per session
    teardownRef.current = true;
    try {
      const settled = await whenWritesSettled({ timeoutMs: 10000 });
      if (!settled) {
        console.warn('LITERACY-APP-WARN: idle sign-out proceeded with a write still in flight (10s cap).');
      }
      if (user) {
        await logSecurityEvent(user, 'IDLE_TIMEOUT_LOGOUT', { reason }).catch(() => {});
      }
      detachAllSnapshots();
      clearSchoolViewCache(); // School View's session cache dies with the token (v3.78.0)
      // v3.80.0: the roster-lock + Grade Team session probes die with it too — on a
      // shared device the next teacher must never see the previous answer.
      clearRosterSourceCache();
      clearGradeTeamSourceCache();
      sessionStorage.removeItem('isImpersonating');
      sessionStorage.removeItem('impersonation_pending');
      adminRestoreTokenRef.current = null;
      await signOut(auth);
    } catch (err) {
      console.error('Idle sign-out error:', err);
    } finally {
      idleRef.current?.clearIdleState();
      const target = reason === 'logout' ? '/login' : '/login?reason=timeout';
      window.location.assign(target);
    }
  };

  const idle = useIdleTimeout(!!user, handleIdleExpiry);

  // v3.91.0: offer "Don't ask for a code on this computer" only in a browser
  // where the global administrator has been signed in (hooks/useTrustOfferFlag.js).
  useTrustOfferFlag(user, isImpersonating);
  useEffect(() => { appUid.current = user?.uid || null; }, [user]);

  // v3.91.0: a signed-in user means the second factor is done — never leave
  // the code screen over the app (hooks/useCloseMfaOnSignIn.js).
  useCloseMfaOnSignIn(user, !!mfaResolver, () => {
    setMfaResolver(null);
    setActiveMfaFactor(null);
    setMfaCode('');
  });
  idleRef.current = idle;

  const handleExitImpersonation = async () => {
    const adminToken = adminRestoreTokenRef.current;
    if (adminToken) {
      setLoading(true);
      try {
        // Sign in as admin first, then do a FULL reload.
        // Both the impersonated user and the admin use custom tokens
        // (providerData.length === 0), so onAuthStateChanged can't tell them apart.
        // A clean reload lets the normal boot sequence pick up the restored admin session.
        await signInWithCustomToken(auth, adminToken);
        
        // Clean up impersonation state BEFORE reload so the fresh boot
        // doesn't think we're still impersonating.
        sessionStorage.removeItem('isImpersonating');
        sessionStorage.removeItem('impersonation_pending');
        
        if (process.env.NODE_ENV === 'development') {
          console.log("LITERACY-APP-SUCCESS: Admin session restored. Reloading...");
        }
        
        // Full reload — the admin's Firebase session is now persisted,
        // so onAuthStateChanged will pick it up cleanly on the fresh boot.
        window.location.href = '/';
      } catch (err) {
        console.error("LITERACY-APP-ERROR: Failed to restore Admin session.");
        setLoading(false);
        alert("Session expired. Please log in normally.");
        handleLogout();
      }
    } else {
      // Fallback if restore token is gone (e.g. hard refresh during impersonation)
      handleLogout();
    }
  };

  if (loading) {
    return <div className="loading-container">Loading data...</div>;
  }

  // Boot-stall recovery (v3.69.2): signed in, but the session pipeline
  // failed or timed out. A visible, retryable state — never the Sign In
  // card over a live session. Reload is the deliberate retry mechanism: a
  // fresh transport is exactly what clears the known WebKit channel stall.
  if (!user && bootStall) {
    return <BootStallNotice email={bootStall.email} detail={bootStall.detail} />;
  }

  return (
    <Router>
      <div id="mfa-recaptcha-container"></div>
      
      {/* MFA Challenge Overlay */}
      {mfaResolver && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: 'white', padding: '40px', borderRadius: '16px', maxWidth: '400px', width: '90%', textAlign: 'center' }}>
            <h2 style={{ marginBottom: '10px' }}>Verification Required</h2>
            <p style={{ color: '#64748b', fontSize: '0.9em', marginBottom: '20px' }}>
              {activeMfaFactor?.type === 'totp' 
                ? 'Enter the 6-digit code from your Authenticator app.'
                : 'A security code was sent to your phone. Please enter it to continue.'}
            </p>
            <form onSubmit={handleMfaSubmit}>
              <input 
                type="text" 
                placeholder="123456"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1', textAlign: 'center', fontSize: '1.2rem', letterSpacing: '4px', marginBottom: '20px' }}
                maxLength={6}
                required
              />
              {trustedComputerSupported() && trustOfferEligible() && (
                <label
                  className="checkbox-row mfa-trust-row"
                  style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', textAlign: 'left', fontSize: '0.9em', color: '#334155', margin: '-6px 0 18px', minHeight: '44px', cursor: 'pointer' }}
                >
                  <input
                    type="checkbox"
                    checked={trustComputer}
                    onChange={(e) => setTrustComputer(e.target.checked)}
                    style={{ width: '20px', height: '20px', margin: '2px 0 0', flex: '0 0 auto' }}
                  />
                  <span>
                    Don't ask for a code on this computer for {TRUST_DAYS} days
                    <span style={{ display: 'block', color: '#64748b', fontSize: '0.9em', marginTop: '2px' }}>
                      Only on a computer you alone use. For the global administrator.
                    </span>
                  </span>
                </label>
              )}

              <button 
                type="submit" 
                disabled={mfaLoading}
                style={{ width: '100%', padding: '14px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold' }}
              >
                {mfaLoading ? 'Verifying...' : 'Verify & Login'}
              </button>
              
              {usableHints(mfaResolver.hints).length > 1 && (
                <button 
                  type="button"
                  onClick={handleMfaSwitch}
                  style={{ width: '100%', padding: '10px', background: 'none', border: '1px solid #cbd5e1', borderRadius: '8px', color: '#64748b', marginTop: '10px', cursor: 'pointer', fontSize: '0.85em' }}
                >
                  Try another way
                </button>
              )}

              <button 
                type="button" 
                onClick={() => { mfaAttempt.current += 1; setMfaResolver(null); signOut(auth); }}
                style={{ background: 'none', border: 'none', color: '#94a3b8', marginTop: '15px', cursor: 'pointer', fontSize: '0.85em' }}
              >
                Cancel login
              </button>
            </form>
          </div>
        </div>
      )}

      {trustNotice && <TrustedComputerNotice notice={trustNotice} onDismiss={() => setTrustNotice(null)} />}

      <DemoBanner />
      {user && <DemoRoleSwitcher active={demoRole} onPick={setDemoRole} />}
      <OfflineBanner />

      {/* Idle warning (v3.56.0). Rendered at the Router level, above the
          route switch, so it covers EVERY authenticated route — not just
          the dashboard. */}
      {user && idle.warningRemainingMs !== null && (
        <IdleWarningModal
          remainingMs={idle.warningRemainingMs}
          onStaySignedIn={idle.extendSession}
          onSignOutNow={() => {
            // Explicit sign-out from the warning: tell sibling tabs first.
            // (The timeout path broadcasts from inside the tracker, and the
            // sidebar Logout broadcasts in handleLogout — this is the third
            // and last way a session can end.)
            idle.broadcastSignOut('logout');
            handleIdleExpiry('logout');
          }}
        />
      )}
      <Suspense fallback={<RouteChunkFallback />}>
      <Routes>
        <Route path="/about-data" element={<AboutData />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/trust-center" element={<TrustCenter />} />
        <Route path="/data-requests" element={<DataRequests />} />
        <Route path="/subprocessors" element={<Subprocessors />} />
        {/* Clever SSO must render pre-auth (the !user catch-all inside
            ProtectedLayout bounces unknown paths to "/") AND while a previous
            user is still signed in — the callback handles the shared-device
            session swap itself. */}
        <Route path="/auth/clever" element={<CleverAuthStart />} />
        <Route path="/auth/clever/callback" element={<CleverCallback />} />
        <Route
          path="/*" 
          element={
            <ProtectedLayout 
              user={user}
              setUser={setUser}
              setExtraMetadata={setExtraMetadata}
              extraMetadata={extraMetadata}
              isNavOpen={isNavOpen}
              setIsNavOpen={setIsNavOpen}
              toggleNav={toggleNav}
              systemModalOpen={!!mfaResolver || (!!user && idle.warningRemainingMs !== null)}
              handleLogout={handleLogout}
              handleExitImpersonation={handleExitImpersonation}
              isImpersonating={isImpersonating}
              handleGoogleLogin={handleGoogleLogin}
              handleEmailLogin={handleEmailLogin}
              handleForgotPassword={handleForgotPassword}
              adminRestoreTokenRef={adminRestoreTokenRef}
              demoRole={demoRole}
            />
          } 
        />
      </Routes>
      </Suspense>
    </Router>
  );
}

/**
 * Boot-stall notice (v3.69.2). Rendered when Firebase IS authenticated but
 * the boot pipeline (license/claims/profile) failed or exceeded
 * LOAD_TIMEOUT_MS — the state that used to render the Sign In card silently
 * (the idle+MFA re-login wedge). "Try again" is a full reload on purpose:
 * a fresh transport is what clears the known WebKit Firestore channel stall.
 * Mobile invariants: both buttons are full-width with ≥44px height and
 * ≥16px text.
 */
function BootStallNotice({ email, detail }) {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: '#f8f9fa', padding: '16px',
      fontFamily: '"Comic Sans MS", "Chalkboard SE", "Comic Neue", sans-serif'
    }}>
      <div role="alert" style={{
        background: 'white', padding: '40px 32px', borderRadius: '16px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.08)', textAlign: 'center',
        width: '100%', maxWidth: '440px', borderTop: '5px solid #f59e0b'
      }}>
        <h1 style={{ fontSize: '1.5rem', color: '#333', marginBottom: '10px' }}>
          You&apos;re signed in — loading didn&apos;t finish
        </h1>
        <p style={{ color: '#475569', fontSize: '1rem', lineHeight: 1.6, marginBottom: '8px' }}>
          Your sign-in{email ? ` as ${email}` : ''} succeeded, but the app
          couldn&apos;t finish loading your session data. This can happen right
          after the inactivity sign-out.
        </p>
        <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '24px' }}>{detail}</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            width: '100%', minHeight: '48px', padding: '14px', backgroundColor: '#3b82f6',
            color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold',
            fontSize: '1.05rem', cursor: 'pointer'
          }}
        >
          Try again
        </button>
        <button
          onClick={() => {
            signOut(auth).catch(() => {}).finally(() => window.location.assign('/login'));
          }}
          style={{
            width: '100%', minHeight: '44px', padding: '12px', background: 'none',
            border: '1px solid #cbd5e1', borderRadius: '8px', color: '#64748b',
            marginTop: '10px', cursor: 'pointer', fontSize: '1rem'
          }}
        >
          Sign out instead
        </button>
      </div>
    </div>
  );
}

/**
 * Self-healing loading screen for a pending impersonation handshake.
 * Reached only when the app has finished booting (loading === false) with no user
 * but a leftover 'impersonation_pending' flag — i.e. the handshake was interrupted
 * (hard refresh mid-impersonation, lost in-memory restore token, etc.). Unlike the
 * normal boot loaders this path used to have no safety timeout, so a stranded flag
 * hung "Loading data..." forever until sessionStorage was cleared. This recovers
 * automatically after a grace period and offers an immediate manual escape.
 */
function recoverFromStrandedImpersonation() {
  sessionStorage.removeItem('impersonation_pending');
  window.location.href = '/login';
}

function ImpersonationPendingScreen() {
  useEffect(() => {
    const t = setTimeout(recoverFromStrandedImpersonation, 8000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="loading-container">
      <div>Loading data...</div>
      <button
        type="button"
        onClick={recoverFromStrandedImpersonation}
        style={{
          marginTop: '20px',
          padding: '8px 16px',
          background: 'none',
          border: '1px solid #cbd5e1',
          borderRadius: '8px',
          color: '#64748b',
          cursor: 'pointer',
          fontSize: '0.85em',
        }}
      >
        Taking too long? Return to login
      </button>
    </div>
  );
}

/**
 * Stable Layout for Protected Routes
 * Moved outside the App component to prevent full remounts when toggleNav changes App state.
 * This ensures that local state within children (e.g. AssessmentForm) is preserved.
 */
function ProtectedLayout({
  user,
  setUser,
  setExtraMetadata,
  extraMetadata,
  isNavOpen,
  setIsNavOpen,
  toggleNav,
  systemModalOpen = false,
  handleLogout,
  handleExitImpersonation,
  isImpersonating,
  handleGoogleLogin,
  handleEmailLogin,
  handleForgotPassword,
  adminRestoreTokenRef,
  demoRole
}) {
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  // Which view Send Feedback opens on: the form (sidebar entry) or Your
  // messages (the home-page reply card).
  const [feedbackView, setFeedbackView] = useState('new');
  const [isReleaseNotesOpen, setIsReleaseNotesOpen] = useState(false);
  const [isSettingsProfileOpen, setIsSettingsProfileOpen] = useState(false);

  // Before rendering anything, attach the current metadata to the user object.
  // CRITICAL FIX: Only override if extraMetadata actually has values. 
  // This prevents the 'Access Denied' flicker where the role is nulled out 
  // for a few frames during state synchronization.
  if (user) {
    if (extraMetadata.profile) user.profile = extraMetadata.profile;
    if (extraMetadata.role) user.role = extraMetadata.role;

    // Demo only: the single demo login carries teacher+admin+tls claims; the
    // role switcher constrains the *view* to one role at a time so each role's
    // experience can be presented separately. Server-side rules still use the
    // real (all-true) claims, so data access is unaffected.
    if (IS_DEMO && user.role) {
      const dr = demoRole || getDemoRole();
      user.role = {
        teacher: dr === 'teacher',
        tls: dr === 'tls',
        admin: dr === 'admin',
        districtAdmin: null,
        districtAdminId: null,
        ea: false,
      };
    }
  }

  // Grade Team (v3.80.0): does this teacher own a class at a building whose
  // registry setting is on? Answered once per session by the probe
  // (gradeTeamSource.js: the roster-lock class query + one cached schools
  // get per building); null while loading so a reload / deep link keeps the
  // route mounted until the answer is in. Gates the route AND the nav entry.
  const gradeTeamEligible = !!user?.uid && !!user?.role?.teacher && isFeatureEnabled('gradeTeam');
  const [gradeTeamCtx, setGradeTeamCtx] = useState(null);
  useEffect(() => {
    if (!gradeTeamEligible) { setGradeTeamCtx({ buildings: [] }); return undefined; }
    let cancelled = false;
    setGradeTeamCtx(null);
    getGradeTeamBuildings(user).then((buildings) => { if (!cancelled) setGradeTeamCtx({ buildings }); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gradeTeamEligible, user?.uid]);

  // Replies to this user's own Send Feedback messages (v3.90.0): one bounded
  // query per signed-in page load, after auth + claims (this layout only has
  // a user once setUser ran). Not on demo, and read-only while an
  // administrator is viewing as the user — both decided inside the hook
  // (hooks/useFeedbackReplies.js → useSessionFeedbackReplies), where they are
  // tested.
  const feedbackReplies = useSessionFeedbackReplies(user, isImpersonating);

  if (!user) {
    // If we're in the middle of a session handshake, don't flash the login screen.
    // Stay in the 'loading context' until the App component finishes the handshake.
    const isImpersonatingPending = sessionStorage.getItem('impersonation_pending') === 'true';
    if (isImpersonatingPending) {
      return <ImpersonationPendingScreen />;
    }

    return (
      <Suspense fallback={<RouteChunkFallback />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage onLogin={handleGoogleLogin} onEmailLogin={handleEmailLogin} onForgotPassword={handleForgotPassword} />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      </Suspense>
    );
  }

  // Force mandatory onboarding ONLY for a CONFIRMED missing profile: the auth
  // handler sets user.profile = null when users/{uid} does not exist
  // (exists() === false). A failed read never reaches here — loadProfileOnce
  // retries once, then the boot-stall notice shows with Try again (v3.84.8).
  // `undefined` means "not resolved" and must never open the setup flow.
  if (user && user.profile === undefined && !user.role?.admin) {
    return <div className="loading-container">Loading data...</div>;
  }
  if (user && user.profile === null && !user.role?.admin) {
    return (
      <OnboardingModal 
        user={user} 
        onComplete={(profileData) => {
          // Update the separate metadata state instead of cloning the user object.
          // This triggers a re-render and keeps the multiFactor() check working.
          setExtraMetadata(prev => ({ ...prev, profile: profileData }));
        }} 
      />
    );
  }

  // Single source of truth for "where does this role belong?" — used by the
  // /dashboard guard and every catch-all redirect below.
  const landingRoute = resolveLandingRoute(user.role, {
    tlsRouteEnabled: isFeatureEnabled('tlsRole'),
  });

  return (
    <OnboardingProvider
      user={user}
      isNavOpen={isNavOpen}
      setIsNavOpen={setIsNavOpen}
      appModalOpen={isFeedbackOpen || isReleaseNotesOpen || isSettingsProfileOpen}
      systemModalOpen={systemModalOpen}
    >
    <div className="app-container">
      <button
        onClick={toggleNav}
        className={`nav-toggle-button ${isNavOpen ? 'nav-open' : ''}`}
        title={isNavOpen ? 'Close Navigation' : 'Open Navigation'}
      >
        {isNavOpen ? '‹' : '☰'}
      </button>

      <SidePanel
        handleLogout={handleLogout}
        handleExitImpersonation={handleExitImpersonation}
        user={user}
        gradeTeamCtx={gradeTeamCtx}
        isOpen={isNavOpen}
        toggleNav={toggleNav}
        isImpersonating={isImpersonating}
        onOpenFeedback={() => { setFeedbackView('new'); setIsFeedbackOpen(true); }}
        onOpenReleaseNotes={() => setIsReleaseNotesOpen(true)}
        onOpenSettingsProfile={() => setIsSettingsProfileOpen(true)}
      />

      <div className={`content-area ${isNavOpen ? 'nav-open' : ''}`}>
        {/* Reply to your feedback (v3.90.0): on the role's home page only. */}
        <FeedbackReplyNotice
          replies={feedbackReplies.replies}
          landingRoute={landingRoute}
          onAcknowledge={feedbackReplies.acknowledge}
          onViewAll={() => { setFeedbackView('mine'); setIsFeedbackOpen(true); }}
          error={feedbackReplies.error}
          readOnly={isImpersonating}
        />
        <ErrorBoundary>
          <Suspense fallback={<RouteChunkFallback />}>
          <Routes>
            {/* Landing resolution lives in ONE place (utils/roleRouting).
                /dashboard is still the teacher roster; anyone whose role
                lands elsewhere (admin console, TLS) is redirected here and
                from the catch-alls below by the same resolver. */}
            <Route path="/dashboard" element={
              landingRoute !== '/dashboard'
                ? <Navigate to={landingRoute} replace />
                : <Dashboard user={user} />
            } />
            <Route path="/assessment" element={<AssessmentForm user={user} />} />
            <Route path="/results" element={<Results user={user} />} />
            {isFeatureEnabled('benchmarkScreener') && (
              <>
                <Route path="/screener/assessment" element={<BenchmarkScreenerAssessment user={user} />} />
                <Route path="/screener/results" element={<BenchmarkScreenerResults user={user} />} />
              </>
            )}
            {isFeatureEnabled('boyReadiness') && (
              <Route path="/boy/readiness" element={<BoyReadiness user={user} />} />
            )}
            {isFeatureEnabled('paScreener') && (
              <>
                <Route path="/pa/screener" element={<PaScreener user={user} />} />
                <Route path="/pa/results" element={<PaScreenerResults user={user} />} />
                <Route path="/pa/groups" element={<PaGroups user={user} />} />
              </>
            )}
            {isFeatureEnabled('pa4kAssessments') && (
              <>
                <Route path="/pa4k/assessment" element={<Pa4kAssessment user={user} />} />
                <Route path="/pa4k/results" element={<Pa4kResults user={user} />} />
              </>
            )}
            {isFeatureEnabled('paDiagnostic') && (
              <>
                <Route path={PAD_ROUTES.assessment} element={<PadAssessment user={user} />} />
                <Route path={PAD_ROUTES.results} element={<PadResults user={user} />} />
              </>
            )}
            {isFeatureEnabled('tlsRole') && user.role?.tls && (
              <>
                <Route path="/school/skill-gaps" element={<SkillGapFinder user={user} />} />
                <Route path="/school/recommended-groups" element={<RecommendedGroups user={user} />} />
                <Route path="/school/groups" element={<SchoolGroups user={user} />} />
              </>
            )}
            {/* School View (v3.78.0): TLS (own building) + global admin (any building);
                flag-gated, so with the flag off the paths fall through to the
                role landing redirect below like any unknown path. */}
            {canSeeSchoolView(user) && (
              <>
                <Route path="/school/view" element={<SchoolView user={user} />} />
                <Route path="/school/view/:schoolId/grade/:gradeSlug" element={<GradeReport user={user} />} />
              </>
            )}
            {/* Reports (v3.79.0): the teacher / EA Class Report; flag-gated like
                School View, so with the flag off the path falls through to the
                role landing redirect below. */}
            {canSeeReports(user) && (
              <Route path="/reports/class" element={<ClassReport user={user} setUser={setUser} />} />
            )}
            {/* Student Report (v3.98.0): one student, every assessment — same gate as the Class Report. */}
            {canSeeReports(user) && (
              <Route path="/reports/student" element={<StudentAssessmentReport user={user} />} />
            )}
            {/* Grade Team (v3.80.0): mounts while the session probe is loading; the
                page itself redirects once the probe says no qualifying building. */}
            {mayMountGradeTeamRoute(user, gradeTeamCtx) && (
              <Route path="/reports/grade-team" element={<GradeTeam user={user} ctx={gradeTeamCtx} landingRoute={landingRoute} />} />
            )}
            <Route path="/practice" element={<PracticeForm user={user} />} />
            <Route path="/groups" element={<Groups user={user} />} />
            <Route path="/hfw/assessment" element={<HFWAssessment user={user} />} />
            <Route path="/hfw/results" element={<HFWResults user={user} />} />
            <Route path="/hfw/practice" element={<HFWPractice user={user} />} />
            <Route path="/hfw/groups" element={<HFWGroups user={user} />} />
            <Route path="/cvc/assessment" element={<CvcAssessment user={user} />} />
            <Route path="/cvc/results" element={<CvcResults user={user} />} />
            <Route path="/cvc/practice" element={<CvcPractice user={user} />} />
            <Route path="/cvc/groups" element={<CvcGroups user={user} />} />
            <Route path="/dolch/assessment" element={<DolchAssessment user={user} />} />
            <Route path="/dolch/results" element={<DolchResults user={user} />} />
            <Route path="/dolch/practice" element={<DolchPractice user={user} />} />
            <Route path="/nonsense-words/assessment" element={<NonsenseAssessment user={user} />} />
            <Route path="/nonsense-words/results" element={<NonsenseResults user={user} />} />
            <Route path="/nonsense-words/practice" element={<NonsensePractice user={user} />} />
            <Route path="/student-management" element={<StudentUpload user={user} />} />
            <Route path="/assessment-management" element={<AssessmentManagement user={user} setUser={setUser} />} />
            <Route path="/display-settings" element={<DisplaySettings />} />
            <Route path="/help" element={<UserGuide user={user} />} />
            <Route path="/debug-screensize" element={<ScreenSizeDebugger />} />
            <Route path="/test-harness" element={<ResponsiveTestHarness />} />
            <Route path="/teacher-report/:studentId" element={<StudentReport user={user} />} />
            {(user.role?.admin || user.role?.districtAdmin) && <Route path="/admin/dashboard" element={<AdminDashboard user={user} existingRestoreToken={adminRestoreTokenRef.current} />} />}
            
            <Route path="/login" element={<Navigate to={landingRoute} replace />} />
            <Route path="/" element={<Navigate to={landingRoute} replace />} />
            <Route path="*" element={<Navigate to={landingRoute} replace />} />
          </Routes>
          </Suspense>
        </ErrorBoundary>

        <footer className="no-print" style={{
          marginTop: '40px',
          padding: '20px 0',
          borderTop: '1px solid #e2e8f0',
          textAlign: 'center',
          color: '#94a3b8',
          fontSize: '0.85rem'
        }}>
          <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap' }}>
            <Link to="/trust-center" style={{ color: '#64748b', textDecoration: 'none' }}>Trust Center</Link>
            <Link to="/privacy" style={{ color: '#64748b', textDecoration: 'none' }}>Privacy Policy</Link>
            <Link to="/terms" style={{ color: '#64748b', textDecoration: 'none' }}>Terms of Service</Link>
          </div>
          &copy; 2026 Yilmaz Education LLC. All rights reserved.
        </footer>
      </div>

      <FeedbackModal
        isOpen={isFeedbackOpen}
        onClose={() => setIsFeedbackOpen(false)}
        user={user}
        initialView={feedbackView}
        isImpersonating={isImpersonating}
        onRepliesSeen={feedbackReplies.forget}
      />
      <ReleaseNotesModal isOpen={isReleaseNotesOpen} onClose={() => setIsReleaseNotesOpen(false)} />
      {isSettingsProfileOpen && (
        <OnboardingModal 
          user={user} 
          isEditMode={true}
          onClose={() => setIsSettingsProfileOpen(false)}
          onComplete={(profileData) => {
            // Update metadata securely; App will re-render and children will see updated user.profile
            setExtraMetadata(prev => ({ ...prev, profile: profileData }));
            setIsSettingsProfileOpen(false);
            alert("Profile settings updated successfully!");
          }} 
        />
      )}
    </div>
    </OnboardingProvider>
  );
}

function SidePanel({ handleLogout, handleExitImpersonation, user, gradeTeamCtx = null, isOpen, toggleNav, isImpersonating, onOpenFeedback, onOpenReleaseNotes, onOpenSettingsProfile }) {
  const location = useLocation();
  // Onboarding (v3.75.0): inert stub when both flags are off or no provider.
  const onboarding = useOnboarding();

  // Determine if we should show Teacher/EA specific tabs
  // Defaults to true if they are explicitly marked as a teacher/EA or if they are completely unassigned (legacy users).
  // Pure global admins, pure district admins, or pure TLS users will evaluate to false.
  // v3.79.0: the predicate lives in src/utils/teacherTabs.js (pure refactor,
  // pinned per role combination) so the Reports gate can never disagree with it.
  const shouldShowTeacherTabs = showsTeacherTabs(user?.role);
  const shouldShowSchoolTabs = !!user?.role?.tls && isFeatureEnabled('tlsRole');
  // School View (v3.78.0): the School group's first entry, for a TLS AND a global admin.
  const shouldShowSchoolView = canSeeSchoolView(user);
  // Reports (v3.79.0): teacher / EA Class Report, flag `reports`.
  const shouldShowReports = canSeeReports(user);
  // Grade Team (v3.80.0): flag + teacher claim + a qualifying building (the probe, passed down from ProtectedLayout).
  const shouldShowGradeTeam = canSeeGradeTeam(user, gradeTeamCtx);
  const roleLabel = resolveRoleLabel(user?.role);

  const COLLAPSIBLE_GROUPS = ['Phonemic Awareness', 'Phonological Awareness (4K)', PAD_SIDEBAR_GROUP, 'Letter/Sound', 'High Frequency Words', 'CVC Words', 'Dolch', 'Nonsense Words', 'Reports', 'School', 'Management', 'Help'];

  const GROUP_ICONS = {
    'Letter/Sound':         '🔡',
    'High Frequency Words': '📚',
    'CVC Words':            '📖',
    'Dolch':                '📚',
    'Nonsense Words':       '🔊',
    'Phonemic Awareness':   '🎧',
    'Phonological Awareness (4K)': '🗣️',
    [PAD_SIDEBAR_GROUP]:    PAD_SIDEBAR_ICON,
    '4K Readiness':         '🌅',
    'Nonsense Word Fluency': '📋',
    'Reports':              '📑',
    'School':               '🏫',
    'Management':           '⚙️',
    'Help':                 '❓',
  };

  const GROUP_COLORS = {
    'Letter/Sound':         '#f59e0b',
    'High Frequency Words': '#0ea5e9',
    'CVC Words':            '#14b8a6',
    'Dolch':                '#8b5cf6',
    'Nonsense Words':       '#ec4899',
    'Phonemic Awareness':   '#7c3aed',
    'Phonological Awareness (4K)': '#0891b2',
    [PAD_SIDEBAR_GROUP]:    PAD_SIDEBAR_COLOR,
    '4K Readiness':         '#f97316',
    'Nonsense Word Fluency': '#10b981',
    'Reports':              '#6366f1',
    'School':               '#0284c7',
    'Management':           '#64748b',
    'Help':                 '#475569',
  };

  const [collapsedGroups, setCollapsedGroups] = React.useState(
    () => Object.fromEntries(COLLAPSIBLE_GROUPS.map(g => [g, true]))
  );

  const toggleGroup = (groupName) => {
    // Help group opening: warm the How-to videos list (one doc read, session-cached) —
    // only when the helpVideos flag is on (the stub's prefetch is a noop anyway).
    if (groupName === NAV_GROUP_HELP && collapsedGroups[groupName] && onboarding.videosEnabled) onboarding.prefetchHelpVideos();
    setCollapsedGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }));
  };

  // Onboarding tour choreography (v3.75.0): lets the provider expand the
  // Letter/Sound and Help groups for their steps and restore them afterwards.
  const collapsedGroupsRef = useRef(collapsedGroups);
  collapsedGroupsRef.current = collapsedGroups;
  const registerNavControls = onboarding.registerNavControls;
  useEffect(() => registerNavControls({
    setGroupCollapsed: (groupName, collapsed) => setCollapsedGroups(prev => (
      prev[groupName] === !!collapsed ? prev : { ...prev, [groupName]: !!collapsed }
    )),
    getCollapsedGroups: () => ({ ...collapsedGroupsRef.current }),
  }), [registerNavControls]);

  // Scrollability hint: when the nav list overflows and the user hasn't scrolled to the
  // bottom yet, show a subtle "▾ More below" cue so they know to scroll for more options.
  const navListRef = useRef(null);
  const [navOverflow, setNavOverflow] = useState({ overflowing: false, atBottom: true });

  useEffect(() => {
    const el = navListRef.current;
    if (!el) return;
    const update = () => {
      const overflowing = el.scrollHeight > el.clientHeight + 1;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
      setNavOverflow({ overflowing, atBottom });
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (ro) ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [isOpen, user]);

  const showScrollHint = isOpen && navOverflow.overflowing && !navOverflow.atBottom;

  // Clever roster lock (v3.64.0): when EVERY active-year class is synced
  // from Clever, the Classes & Students entry greys out (never removed —
  // the page itself explains and stays reachable). One session-cached
  // classes query; see utils/rosterSource.js. Gated on the DATA's source,
  // not the login method (hybrid Google/Clever logins exist).
  const [rosterLock, setRosterLock] = useState(null);
  const [sisName, setSisName] = useState(null);
  useEffect(() => {
    if (!user?.uid || !shouldShowTeacherTabs) { setRosterLock(null); return undefined; }
    let cancelled = false;
    getRosterLock(user).then((lock) => {
      if (cancelled) return;
      setRosterLock(lock);
      if (lock.allClever) {
        getDistrictSisName(user?.profile?.districtId).then((name) => {
          if (!cancelled) setSisName(name);
        });
      }
    });
    return () => { cancelled = true; };
  }, [user, shouldShowTeacherTabs]);
  const rosterLocked = !!rosterLock?.allClever;

  const navItems = [
    // Dashboard: ungrouped — landing page for teachers
    ...(shouldShowTeacherTabs ? [{ path: '/dashboard', label: 'Dashboard', icon: '🏠' }] : []),

    // 4K Readiness group — FIRST nav group (above the literacy groups,
    // per Kemal 2026-08-09: start-of-year checks lead the year). Gated by
    // feature flag; single combined readiness page — once-per-year checks
    // have no rounds, so no separate Results page. When the teacher hides
    // the group (Assessment Settings), the section collapses to zero
    // height+margin — the rest of the nav renders gap-free as before.
    ...(shouldShowTeacherTabs && isFeatureEnabled('paScreener') ? [
      { path: '/pa/screener', label: 'Assessment', group: 'Phonemic Awareness', icon: '🎧' },
      { path: '/pa/results', label: 'Results', group: 'Phonemic Awareness', icon: '📊' },
      { path: '/pa/groups', label: 'Small Groups', group: 'Phonemic Awareness', icon: '👥' },
    ] : []),

    // Phonological Awareness (4K) group (v3.70.0) — six standard-pattern
    // assessments. A DIFFERENT instrument from the "Phonemic Awareness"
    // screener group directly above (own flag, dev-only posture); adjacent
    // on purpose so the two sections read as siblings on dev.
    ...(shouldShowTeacherTabs && isFeatureEnabled('pa4kAssessments') ? [
      { path: '/pa4k/assessment', label: 'Assessment', group: 'Phonological Awareness (4K)', icon: '🗣️' },
      { path: '/pa4k/results', label: 'Results', group: 'Phonological Awareness (4K)', icon: '📊' },
    ] : []),

    // Phonemic Awareness Diagnostic (PA-D, v3.72.0) — the THIRD PA sibling,
    // a fully isolated module (src/pa-diagnostic; routes/strings from its
    // index.js surface). Own dev-only flag; adjacent to the two PA groups
    // above on purpose so the three sections read together on dev.
    ...(shouldShowTeacherTabs && isFeatureEnabled('paDiagnostic') ? [
      { path: PAD_ROUTES.assessment, label: 'Assessment', group: PAD_SIDEBAR_GROUP, icon: PAD_SIDEBAR_ICON },
      { path: PAD_ROUTES.results, label: 'Results', group: PAD_SIDEBAR_GROUP, icon: '📊' },
    ] : []),
    ...(shouldShowTeacherTabs && isFeatureEnabled('boyReadiness') ? [
      { path: '/boy/readiness', label: '4K Readiness', group: '4K Readiness', icon: '🌅' },
    ] : []),

    // Letter/Sound group
    ...(shouldShowTeacherTabs ? [
      { path: '/assessment', label: 'Assessment', group: 'Letter/Sound', icon: '🔤' },
      { path: '/results', label: 'Results', group: 'Letter/Sound', icon: '📊' },
      { path: '/practice', label: 'Practice', group: 'Letter/Sound', icon: '🎯' },
      { path: '/groups', label: 'Small Groups', group: 'Letter/Sound', icon: '👥' },
    ] : []),

    // High Frequency Words group
    ...(shouldShowTeacherTabs ? [
      { path: '/hfw/assessment', label: 'Assessment', group: 'High Frequency Words', icon: '✍️' },
      { path: '/hfw/results', label: 'Results', group: 'High Frequency Words', icon: '📈' },
      { path: '/hfw/practice', label: 'Practice', group: 'High Frequency Words', icon: '🎲' },
      { path: '/hfw/groups', label: 'Small Groups', group: 'High Frequency Words', icon: '👥' },
    ] : []),

    // CVC Words group
    ...(shouldShowTeacherTabs ? [
      { path: '/cvc/assessment', label: 'Assessment', group: 'CVC Words', icon: '✍️' },
      { path: '/cvc/results', label: 'Results', group: 'CVC Words', icon: '📈' },
      { path: '/cvc/practice', label: 'Practice', group: 'CVC Words', icon: '🎲' },
      { path: '/cvc/groups', label: 'Small Groups', group: 'CVC Words', icon: '👥' },
    ] : []),

    // Dolch group
    ...(shouldShowTeacherTabs ? [
      { path: '/dolch/assessment', label: 'Assessment', group: 'Dolch', icon: '📖' },
      { path: '/dolch/results', label: 'Results', group: 'Dolch', icon: '📋' },
      { path: '/dolch/practice', label: 'Practice', group: 'Dolch', icon: '🧩' },
    ] : []),

    // Nonsense Words group
    ...(shouldShowTeacherTabs ? [
      { path: '/nonsense-words/assessment', label: 'Assessment', group: 'Nonsense Words', icon: '🔊' },
      { path: '/nonsense-words/results', label: 'Results', group: 'Nonsense Words', icon: '🔬' },
      { path: '/nonsense-words/practice', label: 'Practice', group: 'Nonsense Words', icon: '🎭' },
    ] : []),

    // Nonsense Word Fluency benchmark group (gated by feature flag)
    ...(shouldShowTeacherTabs && isFeatureEnabled('benchmarkScreener') ? [
      { path: '/screener/assessment', label: 'Assessment', group: 'Nonsense Word Fluency', icon: '📝' },
      { path: '/screener/results', label: 'Results', group: 'Nonsense Word Fluency', icon: '🗂️' },
    ] : []),

    // Reports group (v3.79.0): teacher / EA, flag `reports` (canSeeReports —
    // the same predicate gates the route). v3.80.0 adds Grade Team (flag
    // `gradeTeam` + the teacher claim + a building whose setting is on), the
    // group's second entry — so Reports is collapsible from here on.
    ...(shouldShowReports ? [
      { path: '/reports/class', label: 'Class Report', group: 'Reports', icon: '📄' },
      // v3.98.0: one student, every assessment's scores and notes (printable).
      { path: '/reports/student', label: 'Student Report', group: 'Reports', icon: '🧒' },
    ] : []),
    ...(shouldShowGradeTeam ? [
      { path: '/reports/grade-team', label: 'Grade Team', group: 'Reports', icon: '🤝' },
    ] : []),

    // School group — School View first (TLS + global admin, v3.78.0), then the
    // TLS-only pages; all gated by feature flags.
    ...(shouldShowSchoolView ? [
      { path: '/school/view', label: 'School View', group: 'School', icon: '🏫' },
    ] : []),
    ...(shouldShowSchoolTabs ? [
      { path: '/school/skill-gaps', label: 'Find Students Who Need Help', group: 'School', icon: '🔎' },
      { path: '/school/recommended-groups', label: 'Recommended Groups', group: 'School', icon: '✨' },
      { path: '/school/groups', label: 'My Pull-Out Groups', group: 'School', icon: '👥' },
    ] : []),

    // Management group
    ...(shouldShowTeacherTabs ? [
      {
        path: '/student-management', label: 'Classes & Students', group: 'Management', icon: '👩‍🏫',
        // Greyed (not removed) when the whole roster is Clever-synced: the
        // page stays reachable and explains where changes are really made.
        ...(rosterLocked ? { dimmed: true, dimmedTitle: rosterGuidanceText(sisName) } : {}),
      },
      { path: '/assessment-management', label: 'Assessment Settings', group: 'Management', icon: '🔧' },
      { path: '/display-settings', label: 'Display Settings', group: 'Management', icon: '🖥️' },
    ] : []),

    // Admin Dashboard: ungrouped — only for admins
    ...((user?.role?.admin || user?.role?.districtAdmin) ? [{ path: '/admin/dashboard', label: user?.role?.admin ? 'Admin Dashboard' : 'District Admin Portal', icon: user?.role?.admin ? '🛡️' : '🏛️' }] : []),

    // Help group: User Guide + meta-app actions (What's New, Send Feedback) — always last.
    // Two flags extend it in place (v3.75.0, split in the helpVideos release):
    //   onboardingTour (onboarding.enabled)  → Take the tour
    //   helpVideos (onboarding.videosEnabled) → How-to videos (hidden once the
    //   list is known to be empty) + Contact support
    // With both off the group is exactly User Guide · What's New · Send Feedback.
    { path: '/help', label: 'User Guide', group: 'Help', icon: '📘' },
    ...(onboarding.enabled ? [
      { onClick: () => onboarding.startTour({ returnFocusTo: document.activeElement }), label: 'Take the tour', group: 'Help', icon: '🧭' },
    ] : []),
    ...((onboarding.videosEnabled && !(onboarding.helpVideosStatus === 'loaded' && !onboarding.hasHelpVideos)) ? [
      { onClick: () => onboarding.openHelpVideos(document.activeElement), label: 'How-to videos', group: 'Help', icon: '🎬' },
    ] : []),
    { onClick: onOpenReleaseNotes, label: "What's New", group: 'Help', icon: '✨' },
    { onClick: onOpenFeedback, label: 'Send Feedback', group: 'Help', icon: '💡' },
    ...(onboarding.videosEnabled ? [
      { href: `mailto:${SUPPORT_EMAIL}`, label: 'Contact support', group: 'Help', icon: '✉️' },
    ] : []),
  ];

  const handleLinkClick = () => {
    if (window.innerWidth <= 768) {
      toggleNav();
    }
  };

  return (
    <div className={`side-panel ${isOpen ? 'open' : ''}`}>
      {isOpen
        ? <h3 className="nav-header">Early Literacy App</h3>
        : <div style={{ textAlign: 'center', padding: '18px 0 10px', fontSize: '1.6em' }}>📚</div>
      }

      {user && isOpen && (
        <div className="user-info" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ marginBottom: '10px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            {/* Derived from the app role claim — a TLS/staff or admin user
                is no longer captioned "Teacher:" (v3.56.0). */}
            <span style={{ fontSize: '0.85em', color: '#666', marginBottom: '2px' }}>{roleLabel}:</span>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'nowrap' }}>
              <span style={{ fontWeight: 'bold', fontSize: '0.95em', wordBreak: 'break-word', maxWidth: '170px' }}>
                {user.profile?.displayName || user.displayName || user.email || 'Unknown user'}
              </span>
              {!user.role?.ea && (
                <button
                  onClick={() => {
                    onOpenSettingsProfile();
                    if (window.innerWidth <= 768) toggleNav();
                  }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', marginLeft: '5px', fontSize: '1.2em', flexShrink: 0 }}
                  title="Update Profile & District Settings"
                >
                  ⚙️
                </button>
              )}
            </div>
            <span style={{ fontSize: '0.7em', color: '#888', marginTop: '4px', wordBreak: 'break-all', maxWidth: '180px' }}>
              {user.email}
            </span>
          </div>
        </div>
      )}
      {user && !isOpen && (
        <div
          style={{ textAlign: 'center', padding: '4px 0 8px', fontSize: '1.4em', cursor: !user.role?.ea ? 'pointer' : 'default' }}
          title={user.profile?.displayName || user.displayName || user.email || roleLabel}
          onClick={!user.role?.ea ? () => { onOpenSettingsProfile(); } : undefined}
        >
          👤
        </div>
      )}

      <div
        className={`nav-list${navOverflow.overflowing && !navOverflow.atBottom ? ' has-more-below' : ''}`}
        ref={navListRef}
      >
        {(() => {
          // Build sections: ungrouped items are standalone, grouped items are collected together
          const sections = [];
          let i = 0;
          while (i < navItems.length) {
            const item = navItems[i];
            if (!item.group) {
              sections.push({ type: 'single', item });
              i++;
            } else {
              const groupName = item.group;
              const items = [];
              while (i < navItems.length && navItems[i].group === groupName) {
                items.push(navItems[i]);
                i++;
              }
              sections.push({ type: 'group', groupName, items });
            }
          }

          return sections.map(section => {
            if (section.type === 'single') {
              const isActive = location.pathname === section.item.path || location.pathname.startsWith(section.item.path + '/');
              return (
                <div key={section.item.path} className="nav-item" onClick={handleLinkClick}
                  title={!isOpen ? section.item.label : undefined}>
                  <Link
                    to={section.item.path}
                    className={`nav-link ${isActive ? 'active' : ''}`}
                    style={!isOpen ? { textAlign: 'center', padding: '10px 0', fontSize: '1.35em' } : {}}
                  >
                    {isOpen ? section.item.label : section.item.icon}
                  </Link>
                </div>
              );
            }

            const { groupName, items } = section;
            // Two layers of "hidden":
            //   • FROZEN_HIDDEN groups (Dolch / Nonsense Words) — not in DOM
            //     at all. Reserved for groups not yet ready for teacher
            //     exposure; the toggle UI doesn't show them.
            //   • Toggleable groups the teacher has opted out of —
            //     animated collapse so toggling Save in Assessment
            //     Settings hides the group with a smooth slide instead of
            //     a jarring removal.
            if (FROZEN_HIDDEN.includes(groupName)) return null;
            const isVisible = isGroupVisible(user?.profile, groupName);
            const isCollapsible = COLLAPSIBLE_GROUPS.includes(groupName);
            const isCollapsed = !!collapsedGroups[groupName];
            const groupColor = GROUP_COLORS[groupName] || '#64748b';
            const groupIcon = GROUP_ICONS[groupName] || '📁';

            return (
              <div
                key={`group-${groupName}`}
                data-tour={({ [NAV_GROUP_LETTER_SOUND]: TOUR_TARGETS.navLetterSound, [NAV_GROUP_MANAGEMENT]: TOUR_TARGETS.navManagement, [NAV_GROUP_HELP]: TOUR_TARGETS.navHelp })[groupName]}
                style={{
                  maxHeight: isVisible ? '600px' : '0',
                  opacity: isVisible ? 1 : 0,
                  overflow: 'hidden',
                  transition: 'max-height 0.45s ease, opacity 0.3s ease, margin 0.3s ease',
                  pointerEvents: isVisible ? 'auto' : 'none',
                }}
                aria-hidden={!isVisible}
              >
                <div
                  onClick={isCollapsible && isOpen ? () => toggleGroup(groupName) : undefined}
                  style={{
                    marginTop: '10px', marginBottom: '2px',
                    padding: isOpen ? '4px 12px' : '6px 0',
                    fontSize: isOpen ? '0.65em' : '1.3em',
                    fontWeight: '700',
                    textTransform: isOpen ? 'uppercase' : 'none',
                    letterSpacing: isOpen ? '0.12em' : '0',
                    color: '#fff', background: groupColor,
                    userSelect: 'none',
                    cursor: isCollapsible && isOpen ? 'pointer' : 'default',
                    display: 'flex',
                    justifyContent: isOpen ? 'space-between' : 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                  }}
                  title={!isOpen ? groupName : undefined}
                >
                  {isOpen ? groupName : groupIcon}
                  {isOpen && isCollapsible && (
                    <span style={{
                      fontSize: '1.3em', lineHeight: 1, display: 'inline-block',
                      transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                      transition: 'transform 0.25s ease',
                    }}>▾</span>
                  )}
                </div>
                <div style={{
                  maxHeight: (!isOpen || !isCollapsed) ? '300px' : '0',
                  overflow: 'hidden',
                  transition: 'max-height 0.4s ease-in-out',
                }}>
                  {items.map(item => {
                    const isActive = item.path && (location.pathname === item.path || location.pathname.startsWith(item.path + '/'));
                    const linkClass = `nav-link ${isActive ? 'active' : ''}${item.dimmed ? ' nav-link-dimmed' : ''}`;
                    // Dimmed (Clever roster lock): still a real link — the page
                    // itself carries the full guidance — but visually disabled,
                    // with the message on hover/long-press via title.
                    const linkStyle = {
                      ...(!isOpen ? { textAlign: 'center', padding: '7px 0', fontSize: '1.3em' } : {}),
                      ...(item.dimmed ? { opacity: 0.45, filter: 'grayscale(1)' } : {}),
                    };
                    const linkContent = isOpen ? `› ${item.label}${item.dimmed ? ' 🔒' : ''}` : item.icon;
                    return (
                      <div key={item.path || item.label} className="nav-item" onClick={handleLinkClick}
                        style={{ paddingLeft: isOpen ? '8px' : '0' }}
                        title={item.dimmed ? item.dimmedTitle : (!isOpen ? item.label : undefined)}>
                        {item.path ? (
                          <Link to={item.path} className={linkClass} style={linkStyle}
                            aria-disabled={item.dimmed || undefined}
                            title={item.dimmed ? item.dimmedTitle : undefined}>
                            {linkContent}
                          </Link>
                        ) : item.href ? (
                          <a href={item.href} className={linkClass} style={linkStyle}>
                            {linkContent}
                          </a>
                        ) : (
                          <button
                            type="button"
                            onClick={() => { item.onClick && item.onClick(); }}
                            className={linkClass}
                            style={linkStyle}
                          >
                            {linkContent}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          });
        })()}
      </div>

      {showScrollHint && (
        <div
          className="nav-scroll-hint"
          onClick={() => {
            const el = navListRef.current;
            if (el) el.scrollBy({ top: el.clientHeight * 0.8, behavior: 'smooth' });
          }}
          title="Scroll for more options"
        >
          <span aria-hidden="true">▾</span> More below
        </div>
      )}

      {isImpersonating && (
        <button 
          onClick={handleExitImpersonation || handleLogout} 
          className="impersonation-exit-button"
          style={{ 
            width: 'calc(100% - 20px)', 
            padding: '12px', 
            background: 'linear-gradient(45deg, #ef4444, #f87171)', 
            color: 'white', 
            border: 'none', 
            borderRadius: '8px', 
            fontWeight: 'bold', 
            marginBottom: '10px', 
            cursor: 'pointer',
            fontSize: '0.9em',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}
        >
          Exit Impersonation
        </button>
      )}
      <button onClick={handleLogout} className="logout-button" title={!isOpen ? 'Logout' : undefined}
        style={!isOpen ? { fontSize: '1.4em', padding: '8px 0' } : {}}
      >
        {isOpen ? 'Logout' : '🚪'}
      </button>
    </div>
  );
}

// Test-only export (v3.64.0): App.sidepanel.test.js renders the nav in
// isolation to pin the Clever roster-lock greying. Not a public API.
export { SidePanel };

export default App;
